import type {
  DimensionResult,
  InterpretationStrength,
  ResolvedInterpretation,
  TopicId,
  TriggeredRule,
} from './rules.js';

function applyExclusiveGroups(triggered: readonly TriggeredRule[]): TriggeredRule[] {
  const winners = new Map<string, TriggeredRule>();
  const passthrough: TriggeredRule[] = [];

  for (const trace of triggered) {
    const group = trace.rule.exclusiveGroup;
    if (!group) {
      passthrough.push(trace);
      continue;
    }
    const current = winners.get(group);
    if (!current) {
      winners.set(group, trace);
      continue;
    }
    const better =
      trace.rule.priority > current.rule.priority ||
      (trace.rule.priority === current.rule.priority && trace.rule.weight > current.rule.weight) ||
      (trace.rule.priority === current.rule.priority &&
        trace.rule.weight === current.rule.weight &&
        trace.rule.id.localeCompare(current.rule.id) < 0);
    if (better) winners.set(group, trace);
  }

  return [...passthrough, ...winners.values()].sort(
    (a, b) => b.rule.priority - a.rule.priority || b.rule.weight - a.rule.weight || a.rule.id.localeCompare(b.rule.id),
  );
}

function scoreContribution(trace: TriggeredRule, usedEvidenceGroups: Set<string>): number {
  const base = Math.max(0, trace.rule.weight);
  const fresh = trace.evidenceGroups.filter((group) => !usedEvidenceGroups.has(group)).length;
  const diversityFactor = trace.evidenceGroups.length === 0 ? 1 : 0.5 + 0.5 * (fresh / trace.evidenceGroups.length);
  trace.evidenceGroups.forEach((group) => usedEvidenceGroups.add(group));
  return base * diversityFactor;
}

function strengthLevel(net: number, diversity: number, ruleCount: number): InterpretationStrength['level'] {
  if (ruleCount >= 4 && diversity >= 3 && net >= 140) return 'very_high';
  if (ruleCount >= 3 && diversity >= 2 && net >= 80) return 'high';
  if (ruleCount >= 2 && diversity >= 2 && net >= 30) return 'medium';
  return 'low';
}

export function resolveInterpretation(topic: TopicId, traces: readonly TriggeredRule[]): ResolvedInterpretation {
  const selected = applyExclusiveGroups(traces).filter((trace) => trace.rule.topic === topic);
  const dimensions = new Map<string, { support: number; counter: number; supportIds: string[]; counterIds: string[] }>();
  const usedEvidenceGroups = new Set<string>();

  for (const trace of selected) {
    const contribution = scoreContribution(trace, usedEvidenceGroups);
    for (const effect of trace.rule.effects) {
      const current = dimensions.get(effect.dimension) ?? { support: 0, counter: 0, supportIds: [], counterIds: [] };
      const magnitude = Math.abs(effect.delta) * contribution / 100;
      if (trace.rule.polarity === 'counter' || effect.delta < 0) {
        current.counter += magnitude;
        current.counterIds.push(trace.rule.id);
      } else if (trace.rule.polarity === 'support' || effect.delta > 0) {
        current.support += magnitude;
        current.supportIds.push(trace.rule.id);
      }
      dimensions.set(effect.dimension, current);
    }
  }

  const dimensionResults: DimensionResult[] = [...dimensions.entries()]
    .map(([dimension, value]) => ({
      dimension,
      supportScore: Number(value.support.toFixed(2)),
      counterScore: Number(value.counter.toFixed(2)),
      netScore: Number((value.support - value.counter).toFixed(2)),
      supportingRuleIds: [...new Set(value.supportIds)].sort(),
      counterRuleIds: [...new Set(value.counterIds)].sort(),
    }))
    .sort((a, b) => a.dimension.localeCompare(b.dimension));

  const supportScore = dimensionResults.reduce((sum, item) => sum + item.supportScore, 0);
  const counterScore = dimensionResults.reduce((sum, item) => sum + item.counterScore, 0);
  const diversity = new Set(selected.flatMap((trace) => trace.evidenceGroups)).size;
  const counterRuleCount = selected.filter((trace) => trace.rule.polarity === 'counter').length;
  const strength: InterpretationStrength = {
    level: strengthLevel(supportScore - counterScore, diversity, selected.length),
    supportScore: Number(supportScore.toFixed(2)),
    counterScore: Number(counterScore.toFixed(2)),
    evidenceDiversity: diversity,
    ruleCount: selected.length,
    counterRuleCount,
  };

  return { topic, triggeredRules: selected, dimensions: dimensionResults, strength };
}
